import { normalizeCfItems } from './dynamicContentUtils';
import { selectCardsForRegion } from './geoTargetingUtils';
import { CfNode, ExtendedCfNode } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { getProcessEnvs, logger } from '@marriott/mi-headless-utils';
const { log } = logger({})('fetchCFData');

const isMockCFDataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockCfData = process.env.NEXT_PUBLIC_USE_MOCK_CFDATA;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mockCfData = process.env.USE_MOCK_CFDATA;
  return nextPublicMockCfData === 'true' || mockCfData === 'true';
};

/**
 * Server-side CF fetcher with Basic Auth support.
 * Can consume either mock data or real AEM GraphQL responses.
 *
 * @param tabName - The tab whose Content Fragments to fetch.
 * @param isAuthorMode - Whether AEM author mode is active.
 * @param region - Optional geo-targeting region, sent as the `variation` param so
 *   AEM returns that CF variation (falling back to master when it does not
 *   exist). When omitted, AEM returns the master variation.
 */
export async function fetchCFData(tabName: string, isAuthorMode: boolean, region?: string): Promise<CfNode[]> {
  try {
    log.info(`[fetchCFData] Start for tabName: ${tabName}${region ? `, region: ${region}` : ''}`);
    const isAuthorModeBool = String(isAuthorMode).toLowerCase() === 'true';
    log.info(`[fetchCFData] isAuthorMode: ${isAuthorModeBool}`);
    let nodes: ExtendedCfNode[] = [];

    if (isMockCFDataEnabled()) {
      // local mock import
      const { cfMockData } = await import('../organisms/DynamicTabbedLayout/cfMockContent.mock');
      nodes = selectCardsForRegion<ExtendedCfNode>(cfMockData[tabName as keyof typeof cfMockData]?.items, region);
      log.info(`[fetchCFData] CF nodes count from mock: ${nodes.length}`);
    } else {
      // real AEM GraphQL fetch
      const { NEXT_PUBLIC_AEM_PUBLISH } = getProcessEnvs();
      const AEM_GRAPHQL_BASE = !isAuthorModeBool ? NEXT_PUBLIC_AEM_PUBLISH : '';
      const AEM_GRAPHQL_QUERY_ROOT =
        process.env['AEM_GRAPHQL_QUERY_ROOT'] || 'mi-aem-common-spa/tabcollectionlistvariant';
      const AEM_GRAPHQL_LOCALE = process.env['AEM_GRAPHQL_LOCALE'] || 'en-us';
      const AEM_GRAPHQL_BASIC_AUTH = process.env['NEXT_AEM_AUTH'];

      const tagsJson = JSON.stringify({
        _logOp: 'OR',
        _expressions: [
          {
            value: `${tabName.toLowerCase()}`,
            _operator: 'CONTAINS',
          },
        ],
      });

      // Geo-targeting: the resolved region travels as the `variation` persisted-query
      // param (DPO-87062 / WEB-207197), mirroring the CSR UXL `input.variation`. Sent
      // only when a region resolved; omitted otherwise so AEM serves `master`.
      const variationParam = region ? `;variation=${region}` : '';

      const graphPath = `${AEM_GRAPHQL_QUERY_ROOT}${encodeURIComponent(
        `;tags=${tagsJson};locale=${AEM_GRAPHQL_LOCALE}${variationParam}`
      )}`;

      const url = `${AEM_GRAPHQL_BASE}/graphql/execute.json/${graphPath}`;
      log.info(`[fetchCFData] Fetching CF nodes from AEM url: ${url}`);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (AEM_GRAPHQL_BASIC_AUTH) headers['Authorization'] = AEM_GRAPHQL_BASIC_AUTH;
      log.info(`[fetchCFData] headers: ${JSON.stringify(headers)}`);
      const aemRes = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!aemRes.ok) throw new Error(`[fetchCFData] AEM CF fetch failed: ${aemRes.status} ${aemRes.statusText}`);

      const json = await aemRes.json();
      // Serve the region variation when AEM returned it, else the master baseline
      // (handles both the single-variation contract and multi-variation payloads).
      nodes = selectCardsForRegion<ExtendedCfNode>(json?.data?.tabCollectionList?.items, region);
      log.info(`[fetchCFData] CF nodes count from AEM: ${nodes.length} for tabName: ${tabName}`);
    }
    const normalized = normalizeCfItems(nodes, tabName);
    log.info(`[fetchCFData] Normalized CF nodes count: ${normalized.length} for tabName: ${tabName}`);
    return normalized;
  } catch (err) {
    log.error('[fetchCFData] Error fetching CF nodes:', err);
    return [];
  }
}
